import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Cart drawer extras: "Complete your wardrobe" add buttons and the shipping protection switch.
 *
 * Every request goes through the same CartLinesUpdateEvent flow the theme's product form uses,
 * so the drawer, cart bubble and progress bar re-render from the returned section HTML.
 */

const DECLINED_KEY = 'versel:protection-declined';
const OWN_EVENT = Symbol('versel-cart-extras');

/** @type {Promise<unknown>} */
let queue = Promise.resolve();

const config = () => Theme.shippingProtection ?? { variantId: 0, autoAdd: false };
const protectionVariantId = () => Number(config().variantId) || 0;

const storage = {
  declined() {
    try {
      return localStorage.getItem(DECLINED_KEY) === '1';
    } catch {
      return false;
    }
  },
  /** @param {boolean} value */
  setDeclined(value) {
    try {
      if (value) localStorage.setItem(DECLINED_KEY, '1');
      else localStorage.removeItem(DECLINED_KEY);
    } catch {
      // Storage blocked; the switch still works for this page view.
    }
  },
};

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
}

/**
 * Waits for the theme's own re-render of the drawer to land. Going from an empty to a filled
 * drawer runs inside a view transition, and a second update fired before it lands gets
 * overwritten by the first one's (older) HTML.
 */
async function drawerSettled() {
  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
  for (let attempt = 0; attempt < 40; attempt++) {
    const transition = /** @type {any} */ (document).activeViewTransition;
    if (transition) await transition.finished.catch(() => {});
    if (!document.querySelector('cart-items-component[data-drawer] [data-cart-drawer-empty]')) break;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  await nextFrame();
  await nextFrame();
}

/** @param {any} cart */
const protectionLine = (cart) => cart.items.find((/** @type {any} */ item) => item.variant_id === protectionVariantId());

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * Runs a cart request and announces it so the theme re-renders the cart sections.
 * @param {object} options
 * @param {EventTarget} options.source - Element the event is dispatched from.
 * @param {'add' | 'update' | 'remove'} options.action
 * @param {string} options.url
 * @param {object} options.payload
 * @param {Array<object>} options.lines
 */
function cartRequest({ source, action, url, payload, lines }) {
  const run = async () => {
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise });
    Object.defineProperty(event, OWN_EVENT, { value: true });
    (source.isConnected ? source : document).dispatchEvent(event);

    const sections = cartSectionIds();

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: sections.join(','), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      const failed = Boolean(data.status && data.status !== 200);
      // /cart/change.js answers with the cart; /cart/add.js only with the added lines.
      const cart = !failed && Array.isArray(data.items) && 'item_count' in data ? data : await fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: failed ? undefined : data.sections,
          source: 'versel-cart-extras',
          didError: failed,
        },
      });

      if (failed) throw new Error(data.description || data.message || 'Cart request failed');
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  };

  const next = queue.then(run, run);
  queue = next.catch(() => {});
  return next;
}

/** @param {EventTarget} source */
function addProtection(source) {
  const id = protectionVariantId();
  if (!id) return Promise.resolve();
  return cartRequest({
    source,
    action: 'add',
    url: Theme.routes.cart_add_url,
    payload: { items: [{ id, quantity: 1 }] },
    lines: [{ merchandiseId: String(id), quantity: 1 }],
  });
}

/**
 * @param {EventTarget} source
 * @param {string} lineKey
 * @param {number} quantity
 */
function changeLine(source, lineKey, quantity) {
  return cartRequest({
    source,
    action: quantity > 0 ? 'update' : 'remove',
    url: `${Theme.routes.cart_change_url}.js`,
    payload: { id: lineKey, quantity },
    lines: [{ id: lineKey, quantity }],
  });
}

/* Shipping protection switch */
document.addEventListener('change', (event) => {
  const toggle = event.target;
  if (!(toggle instanceof HTMLInputElement) || !toggle.matches('[data-vc-protection-toggle]')) return;

  const wanted = toggle.checked;
  toggle.disabled = true;
  storage.setDeclined(!wanted);

  // Act on the live cart rather than the rendered markup, which can trail a moment behind.
  fetchCart()
    .then((cart) => {
      const line = protectionLine(cart);
      if (wanted && !line) return addProtection(toggle);
      if (wanted && line.quantity > 1) return changeLine(toggle, line.key, 1);
      if (!wanted && line) return changeLine(toggle, line.key, 0);
    })
    .catch((error) => {
      console.error('[versel-cart-extras]', error);
      toggle.checked = !toggle.checked;
    })
    .finally(() => {
      toggle.disabled = false;
    });
});

/* "Complete your wardrobe" add buttons */
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-vc-upsell-form]')) return;
  event.preventDefault();

  const select = form.querySelector('select[name="id"]');
  const id = Number(new FormData(form).get('id'));
  if (!id) {
    select?.classList.add('is-invalid');
    if (select instanceof HTMLSelectElement) select.focus();
    return;
  }

  const button = form.querySelector('button[type="submit"]');
  if (button instanceof HTMLButtonElement) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }

  cartRequest({
    source: form,
    action: 'add',
    url: Theme.routes.cart_add_url,
    payload: { items: [{ id, quantity: 1 }] },
    lines: [{ merchandiseId: String(id), quantity: 1 }],
  })
    .catch((error) => {
      console.error('[versel-cart-extras]', error);
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    });
});

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('.vc-upsell__select')) return;
  select.classList.remove('is-invalid');

  // Show the chosen variant's image (e.g. the color picked) in the card's gallery.
  const mediaId = select.selectedOptions[0]?.dataset.mediaId;
  const slides = select.closest('.vc-upsell__card')?.querySelector('[data-vc-upsell-slides]');
  if (!mediaId || !(slides instanceof HTMLElement)) return;
  const slide = [...slides.children].find((child) => child instanceof HTMLElement && child.dataset.mediaId === mediaId);
  if (!(slide instanceof HTMLElement)) return;
  // Instant jump: smooth programmatic scrolling is unreliable inside a scroll-snap track.
  slides.scrollTo({ left: slide.offsetLeft - slides.offsetLeft, behavior: 'instant' });
});

/* Gallery dots follow the swiped image. Scroll doesn't bubble, so listen in the capture phase. */
document.addEventListener(
  'scroll',
  (event) => {
    const slides = event.target;
    if (!(slides instanceof HTMLElement) || !slides.matches('[data-vc-upsell-slides]')) return;
    const dots = slides.parentElement?.querySelectorAll('.vc-upsell__dot');
    if (!dots?.length || !slides.clientWidth) return;
    const index = Math.round(Math.abs(slides.scrollLeft) / slides.clientWidth);
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  },
  { capture: true, passive: true }
);

/**
 * Keeps protection in step with the cart: switched on when the first product is added (unless the
 * shopper turned it off), removed when the last product leaves, never more than one.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (/** @type {any} */ (event)[OWN_EVENT]) return;
  const id = protectionVariantId();
  if (!id) return;

  const { action } = /** @type {CartLinesUpdateEvent} */ (event);

  /** @type {CartLinesUpdateEvent} */ (event).promise
    ?.then(async ({ detail }) => {
      if (detail?.didError) return;
      const items = Array.isArray(detail?.items) ? detail.items : (await fetchCart()).items;
      const protection = items.find((/** @type {any} */ item) => item.variant_id === id);
      const products = items.filter((/** @type {any} */ item) => item.variant_id !== id);

      if (products.length === 0) {
        storage.setDeclined(false);
        if (protection) await changeLine(document, protection.key, 0);
        return;
      }

      if (protection && protection.quantity > 1) {
        await changeLine(document, protection.key, 1);
      } else if (!protection && action === 'add' && config().autoAdd && !storage.declined()) {
        await drawerSettled();
        if (!protectionLine(await fetchCart())) await addProtection(document);
      }
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[versel-cart-extras]', error);
    });
});
